import React, { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

export default function Navbar({ onOpenContact }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [userProfileImage, setUserProfileImage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setIsAdmin(userData.isAdmin === true);
            setUserName(userData.name || userData.fullName || currentUser.displayName || currentUser.email.split('@')[0]);
            setUserProfileImage(userData.profileImage || '');
          } else {
            setUserName(currentUser.email.split('@')[0]);
            setUserProfileImage('');
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUserName(currentUser.email.split('@')[0]);
        }
      } else {
        setIsAdmin(false);
        setUserName('');
        setUserProfileImage('');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setUserName('');
      setUserProfileImage('');
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <nav className="bg-slate-900 border-b border-slate-800 text-white px-6 py-4 sticky top-0 z-50 shadow-xl">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        
        {/* Brand Logo */}
        <Link to="/" className="flex items-center gap-2 group">
          <div className="bg-indigo-600 p-2 rounded-xl text-white font-bold text-lg shadow-lg group-hover:bg-indigo-500 transition">
            PF
          </div>
          <span className="font-extrabold text-xl tracking-wide text-white">
            Primium<span className="text-indigo-400">Flats</span>
          </span>
        </Link>
        
        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
          <Link to="/" className="hover:text-indigo-400 transition">Home</Link>
          <Link to="/properties" className="hover:text-indigo-400 transition">Explore Properties</Link>
          <Link to="/about" className="hover:text-indigo-400 transition">About Us</Link>
          
          <button 
            onClick={onOpenContact} 
            className="hover:text-indigo-400 transition bg-transparent border-none cursor-pointer text-sm font-medium text-slate-300"
          >
            Contact
          </button>
        </div>

        {/* Right Side Actions (Desktop) */}
        <div className="hidden md:flex items-center gap-4">
          {isAdmin && (
            <Link 
              to="/admin" 
              className="bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-600 hover:text-white px-4 py-2 rounded-xl text-xs font-semibold transition shadow-sm"
            >
              Admin Dashboard
            </Link>
          )}

          {!user ? (
            <Link 
              to="/auth" 
              className="bg-indigo-600 hover:bg-indigo-700 px-5 py-2.5 rounded-xl text-xs font-semibold transition shadow-lg text-white"
            >
              Login / Signup
            </Link>
          ) : (
            <div className="flex items-center gap-3 bg-slate-800/80 border border-slate-700/60 px-3 py-1.5 rounded-2xl">
              <Link to="/profile" className="flex items-center gap-2.5 text-left hover:opacity-85 transition">
                {userProfileImage ? (
                  <img 
                    src={userProfileImage} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full object-cover border border-indigo-500 shadow"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-xs text-white uppercase shadow">
                    {userName ? userName.charAt(0) : 'U'}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-white max-w-[120px] truncate">
                    {userName || 'User Profile'}
                  </span>
                  <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider">
                    {isAdmin ? 'Administrator' : 'Verified User'}
                  </span>
                </div>
              </Link>
              <div className="h-6 w-[1px] bg-slate-700 mx-1"></div>
              <button 
                onClick={handleLogout}
                className="bg-rose-500/20 text-rose-400 hover:bg-rose-600 hover:text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition border border-rose-500/30"
              >
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Hamburger Button */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-slate-300 hover:text-white focus:outline-none p-2 bg-slate-800 rounded-xl border border-slate-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Dropdown Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-4 pt-4 border-t border-slate-800 flex flex-col gap-3 pb-2">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-800 transition">Home</Link>
          <Link to="/properties" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-800 transition">Explore Properties</Link>
          <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-800 transition">About Us</Link>
          
          <button 
            onClick={() => { setMobileMenuOpen(false); onOpenContact(); }} 
            className="text-left px-4 py-2 rounded-xl text-sm font-medium text-slate-300 hover:bg-slate-800 transition bg-transparent border-none cursor-pointer"
          >
            Contact
          </button>

          {isAdmin && (
            <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 rounded-xl text-sm font-semibold bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 text-center">
              Admin Dashboard
            </Link>
          )}

          {!user ? (
            <Link to="/auth" onClick={() => setMobileMenuOpen(false)} className="w-full bg-indigo-600 text-center py-2.5 rounded-xl text-sm font-semibold mt-2 shadow-lg">
              Login / Signup
            </Link>
          ) : (
            <div className="pt-2 border-t border-slate-800 flex flex-col gap-2">
              <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className="px-4 py-2 bg-slate-800 rounded-xl text-xs text-white font-medium flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {userProfileImage ? (
                    <img src={userProfileImage} alt="Profile" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center text-[10px] font-bold">
                      {userName ? userName.charAt(0) : 'U'}
                    </div>
                  )}
                  <span>{userName}</span>
                </div>
                <span className="text-indigo-400">View &rarr;</span>
              </Link>
              <button onClick={handleLogout} className="w-full bg-rose-600 hover:bg-rose-700 py-2 rounded-xl text-xs font-semibold text-white transition">
                Logout
              </button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}